import React, { useState } from 'react';
import { Snackbar } from '@startuptools/ui/Snackbar';
import { Alert } from '@startuptools/ui/Alert';
import { Button } from '@startuptools/ui/Button';
import { Checkbox } from '@startuptools/ui/Checkbox';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { Box } from '@startuptools/ui/Box';
import { Table } from '@startuptools/ui/Table';
import { IconButton } from '@startuptools/ui/IconButton';
import { Sheet } from '@startuptools/ui/Sheet';
import { InfoTooltip } from '@startuptools/ui/InfoTooltip';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import { GenericError } from '@startuptools/ui/Error';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { Trans, useLingui } from '@lingui/react';
import { ConfirmDialog } from '@startuptools/ui/ConfirmDialog';
import { Actions } from './Actions';
import { useCapTableId } from './hooks/useCapTableId';
import { Identity } from '@startuptools/business-ui/identity/Identity';
import {
  CapTableDocument,
  useCapTableStakeDeleteMutation,
  useCapTableSuspenseQuery,
  useRefreshCapTableMutation,
} from './cap-table.graphql';
import { Row } from './table/Row';
import { StakeTable } from './stake-table';
import { keyBy } from 'lodash-es';
import { GqlCreateIdentityInput } from '../../graphql/base-types.graphql';

export const CapTable = () => {
  const [newOwnerIdentity, setNewOwnerIdentity] = React.useState<GqlCreateIdentityInput | undefined>(undefined);
  const companyId = useCompanyId();
  const capTableId = useCapTableId();
  const [open, setOpen] = React.useState<false | string>(false);
  const { data } = useCapTableSuspenseQuery({
    variables: { companyId, id: capTableId },
    fetchPolicy: 'cache-and-network',
  });
  // eslint-disable-next-line sut/loading-mutation
  const [refresh, { data: refreshData, error: refreshError, reset: resetRefresh }] = useRefreshCapTableMutation({
    refetchQueries: [CapTableDocument],
  });
  // eslint-disable-next-line sut/loading-mutation
  const [deleteStake, { error: deleteStakeError, reset: resetDeleteCapTableStake }] = useCapTableStakeDeleteMutation({
    refetchQueries: [],
  });

  const [clearEdits, setClearEdits] = useState(false);
  const [showSyncDialog, setShowSyncDialog] = useState(false);

  const { i18n } = useLingui();

  const [deleteId, setDeleteId] = useState<string | null>(null);

  const [sortOrder, setSortOrder] = React.useState(data.capTable.stakeholders.map(s => s.id));

  /* only resort when adding/deleting stakeholders, not when e.g. toggling their stakes. */
  React.useEffect(() => {
    if (
      sortOrder.length - data.capTable.stakeholders.length === 1 &&
      // @ts-expect-error TS2345
      !data.capTable.stakeholders.map(s => s.id).includes(sortOrder[0])
    ) {
      // item was just added to sort order on clientside, but not yet available in GQL cache.
      return;
    }
    if (data.capTable.stakeholders.length !== sortOrder.length) {
      setSortOrder(data.capTable.stakeholders.map(s => s.id));
    }
  }, [data.capTable.stakeholders, sortOrder, sortOrder.length]);

  const keyedData = keyBy(data.capTable.stakeholders, 'id');

  if (!data) {
    return (
      <Alert color="warning">
        <Trans id="CapTable not found" />
      </Alert>
    );
  }

  const stakeToBeDeleted = deleteId
    ? data.capTable.stakeholders.flatMap(s => s.stakes).find(r => r.id === deleteId)
    : undefined;

  return (
    <>
      {(refreshError || refreshData) && (
        <Snackbar open onClose={resetRefresh}>
          {refreshError ? (
            <GenericError color="warning" sx={{ width: '100%' }} />
          ) : refreshData ? (
            <Alert>
              <Trans id="Successfully updated CapTable" />
            </Alert>
          ) : null}
        </Snackbar>
      )}

      {deleteId && (
        <ConfirmDialog
          header={<Trans id="Do you really want to delete this stake?" />}
          confirmProps={{ children: <Trans id="Delete" /> }}
          onCancel={() => {
            resetDeleteCapTableStake();
            setDeleteId(null);
          }}
          onConfirm={async () => {
            await deleteStake({ variables: { companyId, input: { id: deleteId } } });
            setDeleteId(null);
          }}
        >
          {`${stakeToBeDeleted?.name}`}
          {deleteStakeError && <GenericError />}
        </ConfirmDialog>
      )}

      <Sheet sx={{ p: 2 }} variant="plain">
        <Stack direction="column" gap={3}>
          {data.capTable.dirty && (
            <Alert
              color="neutral"
              endDecorator={
                <Button
                  onClick={() => {
                    setShowSyncDialog(true);
                  }}
                >
                  <Trans id="Refresh" />
                </Button>
              }
            >
              <Trans id="There's new data to be synced to the cap table. Click to refresh" />
            </Alert>
          )}

          {showSyncDialog && (
            <ConfirmDialog
              header={<Trans id="Do you really want to refresh this CapTable?" />}
              confirmProps={{ children: <Trans id="Refresh" /> }}
              onCancel={() => {
                setShowSyncDialog(false);
              }}
              onConfirm={async () => {
                await refresh({
                  variables: {
                    companyId,
                    input: {
                      id: capTableId,
                      clearEdits,
                    },
                  },
                });
                setShowSyncDialog(false);
              }}
            >
              <Typography>
                <Trans id="This will fetch the latest data from your stakeholder registry and option program. Stakes for cancelled or expired option programs will be deleted." />
              </Typography>

              <Checkbox
                checked={clearEdits}
                onChange={() => {
                  setClearEdits(!clearEdits);
                }}
                label={<Trans id="Delete all manual changes. This will fully reset the CapTable." />}
              />
            </ConfirmDialog>
          )}

          <Actions
            enableExport={data.capTable.stakeholders.length > 0}
            capTable={data.capTable}
            refresh={() => {
              setShowSyncDialog(true);
            }}
            onIdentityCreated={identity => {
              if (!keyedData[identity.id!]) {
                setNewOwnerIdentity(identity);
              }
              setOpen(identity.id ?? false);
            }}
          />

          <Box width="100%" sx={{ overflowX: 'scroll' }}>
            <Table sx={{ minWidth: '1200px', '& > thead > tr > th:nth-child(1)': { width: '30%' } }}>
              <thead>
                <Row
                  component={'th'}
                  slots={{
                    owner: <Trans id="Owner" />,
                    stakes: <Trans id="Stakes" />,
                    value: <Trans id="Current Value" />,
                    acqCost: (
                      <Stack direction="row" spacing={1}>
                        <Trans id="Purchase cost" />

                        <InfoTooltip>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
                            <Typography fontSize="sm">
                              <Trans id="For shares, this is the total purchase cost. For options, it's the number of options multiplied by the sum of the purchase price and strike price." />
                            </Typography>
                            <Typography fontSize="sm" fontWeight="bold" my={1}>
                              <Trans id="Formula (options)" />
                            </Typography>
                            <Typography fontSize="sm">
                              <Trans id="Total Purchase Cost = Number of Shares × (Purchase Price + Strike Price)" />
                            </Typography>
                          </Box>
                        </InfoTooltip>
                      </Stack>
                    ),
                    profit: (
                      <Stack direction="row" spacing={1}>
                        <Trans id="Profit" />
                        <InfoTooltip>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
                            <Typography fontSize="sm">
                              <Trans id="The profit is calculated by multiplying the quantity with the current value minus the purchase cost. Tax not included." />
                            </Typography>
                            <Typography fontSize="sm" fontWeight="bold" my={1}>
                              <Trans id="Formula" />
                            </Typography>
                            <Typography fontSize="sm">
                              <Trans id="Profit = Quantity × (Current Value - Purchase Cost)" />
                            </Typography>
                          </Box>
                        </InfoTooltip>
                      </Stack>
                    ),
                    share: <Trans id="Share" />,
                  }}
                />
              </thead>
              <tbody>
                {newOwnerIdentity && (
                  <>
                    <Row
                      component="td"
                      onClick={() =>
                        setOpen(open => (open === newOwnerIdentity.id ? false : (newOwnerIdentity.id ?? false)))
                      }
                      slots={{
                        owner: (
                          <Stack direction="row" spacing={1}>
                            <IconButton aria-label="expand row" variant="plain" color="neutral" size="sm">
                              {open === newOwnerIdentity.id ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                            </IconButton>
                            <Identity identity={newOwnerIdentity} editable={false} width="90%" />
                          </Stack>
                        ),
                        stakes: null,
                        value: data.capTable.pricePerShare && null,
                        acqCost: null,
                        profit: null,

                        share: null,
                      }}
                    />
                    <tr>
                      <td style={{ height: 0, padding: 0 }} colSpan={6}>
                        {open === newOwnerIdentity.id && (
                          <StakeTable
                            defaultAddState
                            stakes={[]}
                            identity={newOwnerIdentity}
                            stakeholder={{ stake: '0' }}
                            capTableId={data.capTable.id}
                            onCreateStake={() => {
                              setSortOrder(state => [newOwnerIdentity.id!, ...state]);
                              setNewOwnerIdentity(undefined);
                            }}
                          />
                        )}
                      </td>
                    </tr>
                  </>
                )}
                {sortOrder.map(key => {
                  const stakeholder = keyedData[key];
                  if (!stakeholder) return null;
                  return (
                    <>
                      <Row
                        component={'td'}
                        onClick={() => setOpen(open => (open === stakeholder.id ? false : stakeholder.id))}
                        key={stakeholder.id}
                        slots={{
                          owner: (
                            <Stack direction="row" spacing={1}>
                              <IconButton aria-label="expand row" variant="plain" color="neutral" size="sm">
                                {open === stakeholder.id ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                              </IconButton>
                              <Identity identity={stakeholder.identity} editable={false} width="90%" />
                            </Stack>
                          ),
                          stakes: (
                            <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
                              {stakeholder.stake}
                            </NumberFormat>
                          ),
                          value: data.capTable.pricePerShare && (
                            <NumberFormat locale={i18n.locale} variant="currency" currency="SEK">
                              {stakeholder.value}
                            </NumberFormat>
                          ),
                          acqCost: (
                            <NumberFormat locale={i18n.locale} variant="currency" currency="SEK">
                              {stakeholder.acqCost}
                            </NumberFormat>
                          ),
                          profit: (
                            <NumberFormat locale={i18n.locale} variant="currency" currency="SEK">
                              {stakeholder.profit}
                            </NumberFormat>
                          ),

                          share: (
                            <NumberFormat locale={i18n.locale} variant="percentage">
                              {stakeholder.share}
                            </NumberFormat>
                          ),
                        }}
                      />
                      <tr>
                        <td style={{ height: 0, padding: 0 }} colSpan={6}>
                          {open === stakeholder.id && (
                            <StakeTable
                              stakeholder={stakeholder}
                              stakes={stakeholder.stakes}
                              identity={stakeholder.identity}
                              capTableId={data.capTable.id}
                            />
                          )}
                        </td>
                      </tr>
                    </>
                  );
                })}
              </tbody>
              <tfoot>
                <Row
                  component="th"
                  slots={{
                    owner: null,
                    stakes: (
                      <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
                        {data.capTable.stakeholders.reduce((acc, s) => acc + parseInt(s.stake), 0)}
                      </NumberFormat>
                    ),
                    value: data.capTable.pricePerShare && (
                      <NumberFormat locale={i18n.locale} variant="currency" currency="SEK">
                        {data.capTable.stakeholders.reduce((acc, s) => acc + parseInt(s.value ?? '0'), 0)}
                      </NumberFormat>
                    ),
                    acqCost: (
                      <NumberFormat locale={i18n.locale} variant="currency" currency="SEK">
                        {data.capTable.stakeholders.reduce((acc, s) => acc + parseInt(s.acqCost ?? '0'), 0)}
                      </NumberFormat>
                    ),
                    profit: (
                      <NumberFormat locale={i18n.locale} variant="currency" currency="SEK">
                        {data.capTable.stakeholders.reduce((acc, s) => acc + parseInt(s.profit ?? '0'), 0)}
                      </NumberFormat>
                    ),

                    share: (
                      <NumberFormat locale={i18n.locale} variant="percentage">
                        {data.capTable.stakeholders.reduce((acc, s) => acc + parseInt(s.share ?? '0'), 0)}
                      </NumberFormat>
                    ),
                  }}
                />
              </tfoot>
            </Table>
          </Box>
        </Stack>
      </Sheet>
    </>
  );
};
